import React, { useEffect, useState } from 'react';
import { useNavigate, useRouteError } from 'react-router-dom';
import { Home, ArrowLeft, AlertTriangle, Wifi, WifiOff, RefreshCw } from 'lucide-react';

const FLOATING_DOTS = [
  { size: 6, top: '15%', left: '8%',  delay: '0s',   duration: '6s'  },
  { size: 4, top: '25%', left: '85%', delay: '1s',   duration: '8s'  },
  { size: 8, top: '60%', left: '5%',  delay: '2s',   duration: '7s'  },
  { size: 3, top: '75%', left: '90%', delay: '0.5s', duration: '9s'  },
  { size: 5, top: '40%', left: '92%', delay: '3s',   duration: '6.5s'},
  { size: 7, top: '85%', left: '15%', delay: '1.5s', duration: '8.5s'},
  { size: 4, top: '10%', left: '55%', delay: '2.5s', duration: '7.5s'},
  { size: 3, top: '50%', left: '50%', delay: '4s',   duration: '10s' },
];

// Shared page shell (gradient background + floating ambient dots)
function PageShell({ children }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: 'var(--gradient-secondary)' }}
    >
      {/* Ambient blobs */}
      <div
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{ background: 'var(--gradient-primary)' }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{ background: 'var(--gradient-button)' }}
      />

      {/* Floating dots */}
      {FLOATING_DOTS.map((d, i) => (
        <div
          key={i}
          className="absolute rounded-full opacity-20 pointer-events-none"
          style={{
            width: d.size,
            height: d.size,
            top: d.top,
            left: d.left,
            background: 'var(--gradient-primary)',
            animation: `float ${d.duration} ease-in-out ${d.delay} infinite`,
          }}
        />
      ))}

      {/* Content */}
      <div className="relative z-10 w-full max-w-lg animate-slide-in-up">
        {children}
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) scale(1); }
          50%       { transform: translateY(-20px) scale(1.1); }
        }
      `}</style>
    </div>
  );
}

// ── 404 Page ──────────────────────────────────────────────────────────────────
export function NotFoundPage() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  return (
    <PageShell>
      <div
        className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-2xl shadow-elegant overflow-hidden transition-all duration-500"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(16px)' }}
      >
        {/* Top gradient bar */}
        <div className="h-1 w-full" style={{ background: 'var(--gradient-button)' }} />

        <div className="p-8 sm:p-10 text-center space-y-6">
          {/* Giant "404" */}
          <div className="relative inline-block select-none">
            <span
              className="text-[7rem] sm:text-[9rem] font-black leading-none bg-gradient-to-r from-primary to-secondary-cyan bg-clip-text text-transparent"
              style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
            >
              404
            </span>
            {/* Glow shadow */}
            <span
              className="absolute inset-0 text-[7rem] sm:text-[9rem] font-black leading-none opacity-20 blur-2xl pointer-events-none"
              style={{ color: 'hsl(var(--primary))' }}
              aria-hidden
            >
              404
            </span>
          </div>

          {/* Icon */}
          <div className="flex justify-center">
            <div className="p-4 bg-primary/10 rounded-2xl border border-primary/20 pulse-glow">
              <WifiOff className="w-8 h-8 text-primary" />
            </div>
          </div>

          {/* Heading + description */}
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-foreground">Page Not Found</h1>
            <p className="text-muted-foreground leading-relaxed">
              The page you're looking for doesn't exist or has been moved.
            </p>
            <p className="text-xs text-muted-foreground/60 font-mono break-all">
              {window.location.pathname}
            </p>
          </div>

          {/* Divider */}
          <div className="h-px bg-border/50" />

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-muted/60 border border-border/50 text-foreground rounded-xl hover:bg-muted hover:shadow-md transition-all duration-300 font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Go Back
            </button>
            <button
              onClick={() => navigate('/developer/login', { replace: true })}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-button text-primary-foreground rounded-xl hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 font-medium"
            >
              <Home className="w-4 h-4" />
              Go Home
            </button>
          </div>
        </div>
      </div>
    </PageShell>
  );
}

// ── Error Page (router / data errors) ─────────────────────────────────────────
export function ErrorPage() {
  const navigate = useNavigate();
  const error = useRouteError?.();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  const status = error?.status;
  const is404 = status === 404;

  const title = is404 ? 'Page Not Found' : 'Something Went Wrong';
  const description = is404
    ? "The page you're looking for doesn't exist or has been moved."
    : error?.message || error?.statusText || 'An unexpected error occurred. Please try again.';

  return (
    <PageShell>
      <div
        className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-2xl shadow-elegant overflow-hidden transition-all duration-500"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(16px)' }}
      >
        {/* Top gradient bar */}
        <div className="h-1 w-full" style={{ background: is404 ? 'var(--gradient-button)' : 'linear-gradient(135deg, hsl(var(--destructive)), hsl(var(--warning)))' }} />

        <div className="p-8 sm:p-10 text-center space-y-6">
          {/* Status code or icon */}
          <div className="relative inline-block select-none">
            <span
              className="text-[7rem] sm:text-[9rem] font-black leading-none bg-clip-text text-transparent"
              style={{
                backgroundImage: is404
                  ? 'var(--gradient-button)'
                  : 'linear-gradient(135deg, hsl(var(--destructive)), hsl(var(--warning)))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {status || '!'}
            </span>
          </div>

          {/* Icon */}
          <div className="flex justify-center">
            <div className={`p-4 rounded-2xl border ${is404 ? 'bg-primary/10 border-primary/20' : 'bg-destructive/10 border-destructive/20'}`}>
              <AlertTriangle className={`w-8 h-8 ${is404 ? 'text-primary' : 'text-destructive'}`} />
            </div>
          </div>

          {/* Heading + description */}
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-foreground">{title}</h1>
            <p className="text-muted-foreground leading-relaxed">{description}</p>
            <p className="text-xs text-muted-foreground/60 font-mono break-all">
              {window.location.pathname}
            </p>
          </div>

          {/* Divider */}
          <div className="h-px bg-border/50" />

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-muted/60 border border-border/50 text-foreground rounded-xl hover:bg-muted hover:shadow-md transition-all duration-300 font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              Reload Page
            </button>
            <button
              onClick={() => navigate('/developer/login', { replace: true })}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-button text-primary-foreground rounded-xl hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 font-medium"
            >
              <Home className="w-4 h-4" />
              Go Home
            </button>
          </div>
        </div>
      </div>
    </PageShell>
  );
}

export default NotFoundPage;
